import React, { useMemo, useState } from 'react'
import CanvasLogo from '../../../assets/icons/canvas-logo.svg'
import IconMinusCircle from '../../../assets/icons/icon-minus-circle.svg'
import {
  isCompleteStatus,
  isLockedStatus,
  getEnrollmentDate,
  getRequirementDisplayData,
  shouldInferLevelComplete,
  getLevelKeyFromCode,
  buildLevelTrackerData
} from './utils'
import { buildInfoUrl } from './utils/urlUtils'
import LevelCompleteBanner from './LevelCompleteBanner'
import IncompletePATRequirementCard from './IncompletePATRequirementCard'
import AdditionalRequirementsList from './AdditionalRequirementsList'
import MilestoneActionLinks from './MilestoneActionLinks'
import LockIconSvg from '../../../assets/icons/icon-lock-status.svg'
import LockProgressSvg from '../../../assets/icons/icon-lock-progress.svg'
import {
  Container,
  Header,
  Title,
  AssociateHeader,
  Divider,
  ProgressSection,
  LevelTracker,
  LevelItem,
  LevelLabel,
  LevelInfo,
  LevelIndicatorDot,
  LevelCompleteIcon,
  LevelInProgressIcon,
  LevelWarningIcon,
  LevelLockIcon,
  ProgressBar,
  ProgressFill,
  ProgramInfoItem,
  ProgramInfoLabel,
  ProgramInfoLink,
  MilestonesGrid,
  MilestoneCard,
  MilestoneHeader,
  MilestoneTitle,
  StatusBadgeSmall,
  StatusBadgeText,
  StatusBadgeCheckmark,
  StatusBadgeLock,
  MilestoneDescription,
  MilestoneInfoBanner,
  MilestoneInfoText,
  TextButton,
  PortalButton,
  PortalButtonText
} from './Styles/ProgressTracker.styles'

const LevelIndicator = ({ completed, active, locked, hasIncompleteRequirements }) => {
  if (hasIncompleteRequirements) {
    return <LevelWarningIcon src={IconMinusCircle} alt="" />
  }
  if (completed) {
    return <LevelCompleteIcon />
  }
  if (locked) {
    return <LevelLockIcon src={LockProgressSvg} alt="Locked" />
  }
  if (active) {
    return <LevelInProgressIcon />
  }
  return <LevelIndicatorDot />
}

const ProgressTracker = ({
  levelState,
  activeLevel = 'qualifying',
  onLevelClick,
  programHistory = [],
  prospect,
  currentLevelCode
}) => {
  const { patRequirement = null, shouldShowPATWarning = false } = levelState || {}

  const levels = useMemo(() => {
    const baseLevels = buildLevelTrackerData(levelState, activeLevel, programHistory, currentLevelCode)
    
    // Add warning indicator to qualifying level if PAT is incomplete
    return baseLevels.map(level => ({
      ...level,
      hasIncompleteRequirements: level.key === 'qualifying' && shouldShowPATWarning
    }))
  }, [levelState, activeLevel, programHistory, currentLevelCode, shouldShowPATWarning])

  const currentProgramLevel = useMemo(() => 
    programHistory?.find(p => getLevelKeyFromCode(p.levelCode) === activeLevel),
    [programHistory, activeLevel]
  )

  const shouldShowLevelCompleteBanner = useMemo(() =>
    shouldInferLevelComplete(activeLevel, programHistory, currentLevelCode, levelState, prospect),
    [activeLevel, programHistory, currentLevelCode, levelState, prospect]
  )

  const currentLevel = levelState[activeLevel]
  const enrollmentDate = getEnrollmentDate(programHistory)
  const levelCompletionDate = currentProgramLevel?.endDate || null

  // Show PAT warning banner on qualifying level when user has advanced but PAT is incomplete
  const patRequirementWithWarning = activeLevel === 'qualifying' && shouldShowPATWarning ? patRequirement : null

  // Show PAT in additional requirements on levels 1-3 when incomplete
  const additionalRequirements = activeLevel !== 'qualifying' && patRequirement ? [patRequirement] : []

  return (
    <Container>
      <Header>
        <Title>My Path to Membership</Title>
        {!!enrollmentDate && (
          <AssociateHeader>
            <ProgramInfoItem>
              <ProgramInfoLabel>Associate Program</ProgramInfoLabel>
              <ProgramInfoLink>Details ›</ProgramInfoLink>
            </ProgramInfoItem>
            <Divider />
            <ProgramInfoItem>
              <ProgramInfoLabel>Enrolled: {enrollmentDate}</ProgramInfoLabel>
              <ProgramInfoLink>Guidelines ›</ProgramInfoLink>
            </ProgramInfoItem>
          </AssociateHeader>
        )}
      </Header>

      <ProgressSection>
        <LevelTracker>
          {levels.map((level) => (
            <LevelItem 
              key={level.key} 
              active={level.active}
              onClick={() => onLevelClick && onLevelClick(level.key)}
            >
              <LevelInfo>
                <LevelLabel>{level.name}</LevelLabel>
                <LevelIndicator 
                  completed={level.completed} 
                  active={level.active}
                  locked={level.locked}
                  hasIncompleteRequirements={level.hasIncompleteRequirements}
                />
              </LevelInfo>
              <ProgressBar completed={level.completed}>
                <ProgressFill percentage={level.progress} />
              </ProgressBar>
            </LevelItem>
          ))}
        </LevelTracker>
      </ProgressSection>

      {shouldShowLevelCompleteBanner ? (
        <>
          <LevelCompleteBanner
            levelName={currentLevel.name}
            completionDate={levelCompletionDate}
          />
          {patRequirementWithWarning && (
            <MilestonesGrid>
              <IncompletePATRequirementCard
                requirement={patRequirementWithWarning}
                activeLevel={activeLevel}
              />
            </MilestonesGrid>
          )}
          <AdditionalRequirementsList
            additionalRequirements={additionalRequirements}
            onLevelClick={onLevelClick}
          />
        </>
      ) : (
        <MilestonesGrid>
          {currentLevel.requirements.map((requirement) => {
            const displayData = getRequirementDisplayData(activeLevel, requirement)
            const isComplete = isCompleteStatus(requirement.status)
            const isLocked = isLockedStatus(requirement.status)

            const isPATWithWarning = 
              requirement.id === 'playing_ability_test' && shouldShowPATWarning

            return (
              <MilestoneCard key={requirement.id} hasWarning={isPATWithWarning}>
                {isPATWithWarning && (
                  <MilestoneInfoBanner>
                    <MilestoneInfoText>
                      A passing score is needed before enrolling as a PGA Member.{' '}
                      {displayData.infoLinkText && displayData.infoLinkUrl && (
                        <TextButton
                          as="a"
                          href={buildInfoUrl(displayData.infoLinkUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {displayData.infoLinkText}
                        </TextButton>
                      )}
                    </MilestoneInfoText>
                  </MilestoneInfoBanner>
                )}
                <MilestoneHeader>
                  <MilestoneTitle>{displayData.name}</MilestoneTitle>
                  <StatusBadgeSmall
                    backgroundColor={displayData.backgroundColor}
                  >
                    {isComplete && <StatusBadgeCheckmark color={displayData.color} />}
                    {isLocked && <StatusBadgeLock src={LockIconSvg} alt="Locked" />}
                    <StatusBadgeText
                      textColor={displayData.color}
                    >
                      {displayData.label}
                    </StatusBadgeText>
                  </StatusBadgeSmall>
                </MilestoneHeader>
                <MilestoneDescription>
                  {isPATWithWarning 
                    ? 'Congratulations, you have qualifying scores and can advance to Level 1. You will still need to pass the PAT in order to enroll as a PGA of America Professional by the end of Level 3.'
                    : displayData.message
                  }
                  {!isPATWithWarning && displayData.infoLinkText && displayData.infoLinkUrl && (
                    <>
                      {' '}
                      <TextButton
                        as="a"
                        href={buildInfoUrl(displayData.infoLinkUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {displayData.infoLinkText}
                      </TextButton>
                    </>
                  )}
                </MilestoneDescription>
                <MilestoneActionLinks
                  displayData={displayData}
                  requirementStatus={requirement.status}
                />
              </MilestoneCard>
            )
          })}

          <AdditionalRequirementsList
            additionalRequirements={additionalRequirements}
            onLevelClick={onLevelClick}
          />
        </MilestonesGrid>
      )}

      <PortalButton>
        <img src={CanvasLogo} alt="Canvas Logo" />
        <PortalButtonText>Education Portal</PortalButtonText>
      </PortalButton>
    </Container>
  )
}

export default ProgressTracker
